/*!
 * mediaFolder.js — a folder from this computer, for the cues to read
 * © 2026 Rob Canning — GPLv3
 *
 * Picked (folder(open), the folder icon on live.html) or dropped on the page,
 * a folder's sounds, film, pictures and text stay in the browser: nothing is
 * uploaded. The cues ask here before the server -- audio(src:) and audioPool()
 * (audioShared.js, audioPool.js), video() (video.js), image() (image.js),
 * text() (text.js) -- so Oscilla Live on a static host plays your own files,
 * and with no folder picked everything is found as before.
 *
 * A name is found as written from the folder's top (808/kick.wav), then in
 * the kind's own subfolder (audio/808/kick.wav: a folder laid out like a
 * project's), then -- a bare name only -- anywhere, when one file has it.
 * The projector window (follow=1) uses the folder of the window that opened it.
 */

const KINDS = {
  audio: /\.(wav|aif|aiff|mp3|ogg|webm|aac|flac|m4a)$/i,   // as server.js handleAudioTree lists
  video: /\.(mp4|webm|ogv|mov|m4v)$/i,
  images: /\.(png|jpe?g|gif|webp|avif|svg)$/i,
  text: /\.(txt|csv|tsv|json|md)$/i,
};

let own = null;                 // { name, files: Map(path -> File) }
const urls = new Map();         // File -> blob URL, made when first asked for

/** This window's folder, or the opener's (the projector's laptop). */
function current() {
  if (own) return own;
  try { return window.opener?.oscillaMediaFolder?.() ?? null; } catch { return null; }   // another origin's opener
}
window.oscillaMediaFolder = () => own;

const kindOf = path => Object.keys(KINDS).find(k => KINDS[k].test(path)) ?? null;
const baseName = path => path.slice(path.lastIndexOf("/") + 1);

/**
 * The folder's files, by their path from its top: [{ path, file }]. Anything
 * not a sound, film, picture or text is left out, and hidden files. Replaces
 * the folder there was. What the cues cached of it is let go (the event).
 */
export function setFolder(name, entries) {
  forget();
  const files = new Map();
  for (const { path, file } of entries) {
    if (path.split("/").some(p => p.startsWith(".")) || !kindOf(path)) continue;
    files.set(path, file);
  }
  own = { name, files };
  window.dispatchEvent(new CustomEvent("oscilla:media-folder"));
  return folderInfo();
}

export function clearFolder() {
  const had = !!own;
  forget();
  own = null;
  if (had) window.dispatchEvent(new CustomEvent("oscilla:media-folder"));
  return had;
}

function forget() {
  for (const url of urls.values()) URL.revokeObjectURL(url);
  urls.clear();
}

/** { name, audio, video, images, text } -- how many of each -- or null. */
export function folderInfo() {
  const f = current();
  if (!f) return null;
  const n = { name: f.name, audio: 0, video: 0, images: 0, text: 0 };
  // .webm is counted as film; audioPool still plays it, as the server's lists do
  for (const path of f.files.keys()) n[kindOf(path)]++;
  return n;
}

/** Every path, sorted. */
export function folderPaths() {
  return [...(current()?.files.keys() ?? [])].sort();
}

/** The places a name may be, in order: as written, then under the kind's folder. */
const placesFor = (name, kind) => [name, `${kind}/${name}`];

/** The File a cue names, or null: not in the folder, two files have that bare name, or no folder. */
export function findFile(name, kind) {
  const f = current();
  if (!f || !name) return null;
  const clean = String(name).replace(/^\.?\//, "");
  for (const p of placesFor(clean, kind)) if (f.files.has(p)) return f.files.get(p);
  if (clean.includes("/")) return null;
  const same = [...f.files.keys()].filter(p => baseName(p) === clean);
  return same.length === 1 ? f.files.get(same[0]) : null;
}

/** A URL for a File the folder holds: for <video>, <image>, which take no File. */
export function urlOf(file) {
  if (!urls.has(file)) urls.set(file, URL.createObjectURL(file));
  return urls.get(file);
}

/**
 * The names in one of the folder's folders, for a pool (audioPool(path:),
 * image(path:)): the kind's files directly in it, as the server lists them,
 * at `dir` or under the kind's folder. null when no folder has it -- the
 * server is asked then.
 */
export function listDir(dir, kind) {
  const f = current();
  if (!f) return null;
  const clean = String(dir ?? "").replace(/^\.?\/|\/$/g, "");
  for (const base of clean ? placesFor(clean, kind) : ["", `${kind}`]) {
    const prefix = base ? `${base}/` : "";
    const names = [];
    for (const path of f.files.keys()) {
      if (!path.startsWith(prefix)) continue;
      const rest = path.slice(prefix.length);
      if (!rest.includes("/") && KINDS[kind].test(rest)) names.push(rest);
    }
    if (names.length) return names.sort();
  }
  return null;
}

/**
 * A pool's files that match glob: (* any run, ? one character, case not
 * minded) and format: (an extension), when given.
 */
export function filterPool(files, { glob, format } = {}) {
  let out = files;
  if (glob) {
    const re = new RegExp(`^${String(glob).replace(/[.+^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*").replace(/\?/g, ".")}$`, "i");
    out = out.filter(f => re.test(f));
  }
  if (format) {
    const ext = `.${String(format).replace(/^\./, "").toLowerCase()}`;
    out = out.filter(f => f.toLowerCase().endsWith(ext));
  }
  return out;
}

/**
 * Why a file was not found, for the end of a cue's error: what the folder has
 * of that name, or, with no folder and no server project (a static host),
 * how to give one. "" when there is nothing to add.
 */
export function missNote(name, kind) {
  const f = current();
  const clean = String(name ?? "").replace(/^\.?\//, "");
  if (!f) return window.currentProject ? "" : " -- load a folder of your own files: folder(open), or drop it on the page";
  const paths = [...f.files.keys()];
  const same = clean.includes("/") ? [] : paths.filter(p => baseName(p) === clean);
  if (same.length > 1) return ` -- folder "${f.name}" has ${same.length} called ${clean}: ${same.slice(0, 4).join(", ")}${same.length > 4 ? ", ..." : ""}; give the path`;
  const lower = clean.toLowerCase();
  const near = paths.find(p => placesFor(lower, kind).includes(p.toLowerCase()) || (!clean.includes("/") && baseName(p).toLowerCase() === lower));
  if (near) return ` -- folder "${f.name}" has ${near}: the case differs`;
  return ` -- not in folder "${f.name}" either`;
}

/** "folder samples: 42 audio, 3 video ..." -- what is loaded, for the console. */
export function describeFolder() {
  const n = folderInfo();
  if (!n) return "no folder: folder(open), or drop one on the page";
  const counts = [["audio", "audio"], ["video", "video"], ["images", "pictures"], ["text", "text"]]
    .filter(([k]) => n[k]).map(([k, word]) => `${n[k]} ${word}`);
  return `folder ${n.name}: ${counts.join(", ") || "no sound, film, pictures or text"} (in this browser, nothing uploaded)`;
}

// ── picking and dropping ─────────────────────────────────────────────────

/**
 * Ask for a folder (from a click or a key: browsers open no picker otherwise).
 * Chromium's own picker, which asks to let the page see the files; elsewhere
 * a folder input (Chromium's says "upload", which nothing is). The folder's
 * description, or null when none was picked.
 */
export async function pickFolder() {
  if (window.showDirectoryPicker) {
    let dir;
    try { dir = await window.showDirectoryPicker({ id: "oscilla-media" }); }
    catch (err) { if (err.name === "AbortError") return null; throw err; }
    return setFolder(dir.name, await fromHandle(dir)) && describeFolder();
  }
  const input = Object.assign(document.createElement("input"), { type: "file", multiple: true, webkitdirectory: true });
  const files = await new Promise(resolve => {
    input.addEventListener("change", () => resolve([...input.files]), { once: true });
    input.addEventListener("cancel", () => resolve([]), { once: true });
    input.click();
  });
  if (!files.length) return null;
  // webkitRelativePath: "samples/808/kick.wav", the folder's own name first
  const name = files[0].webkitRelativePath.split("/")[0];
  return setFolder(name, files.map(file => ({ path: file.webkitRelativePath.split("/").slice(1).join("/"), file }))) && describeFolder();
}

async function fromHandle(dir, prefix = "", out = []) {
  for await (const [name, h] of dir.entries()) {
    if (name.startsWith(".")) continue;
    if (h.kind === "directory") await fromHandle(h, `${prefix}${name}/`, out);
    else if (kindOf(name)) out.push({ path: prefix + name, file: await h.getFile() });
  }
  return out;
}

/** A drag that carries files (a folder among them): the page shows where it may land. */
export const carriesFiles = e => [...(e.dataTransfer?.types ?? [])].includes("Files");

/**
 * Files dropped on the page: one folder is the folder, by its name; several
 * things dropped are kept under their own names. The description, or null.
 */
export async function dropFolder(dataTransfer) {
  // the entries are the drop event's own, taken before anything is awaited
  const entries = [...dataTransfer.items].map(i => i.webkitGetAsEntry?.()).filter(Boolean);
  if (!entries.length) return null;
  const out = [];
  const one = entries.length === 1 && entries[0].isDirectory;
  if (one) await fromEntry(entries[0], "", out);
  else for (const e of entries) await (e.isDirectory ? fromEntry(e, `${e.name}/`, out) : pushEntry(e, "", out));
  return setFolder(one ? entries[0].name : "dropped files", out) && describeFolder();
}

async function fromEntry(dir, prefix, out) {
  const reader = dir.createReader();
  // readEntries gives a hundred or so at a time, then [] at the end
  for (let batch; (batch = await new Promise((ok, no) => reader.readEntries(ok, no))).length;) {
    for (const e of batch) {
      if (e.name.startsWith(".")) continue;
      await (e.isDirectory ? fromEntry(e, `${prefix}${e.name}/`, out) : pushEntry(e, prefix, out));
    }
  }
  return out;
}

async function pushEntry(entry, prefix, out) {
  if (kindOf(entry.name)) out.push({ path: prefix + entry.name, file: await new Promise((ok, no) => entry.file(ok, no)) });
}
